import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

export type SparkEffectProps = {
  className?: string
  /** Canvas `id` (varsayılan: sparks) */
  canvasId?: string
  amount?: number
  speed?: number
  lifetime?: number
  direction?: { x: number; y: number }
  size?: [number, number]
  maxopacity?: number
  /** `rgba` için "r, g, b" — boşluksuz veya virgüllü: `100,111,248` */
  color?: string
  randColor?: boolean
  acceleration?: [number, number]
}

type SparkOpt = {
  amount: number
  speed: number
  lifetime: number
  direction: { x: number; y: number }
  size: [number, number]
  maxopacity: number
  color: string
  randColor: boolean
  acceleration: [number, number]
  width: number
  height: number
}

type SparkParticle = {
  x: number
  y: number
  age: number
  acceleration: number
  color: string
}

function rand(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function createSpark(opt: SparkOpt): SparkParticle {
  const pad = 200
  return {
    x: rand(-pad, opt.width + pad),
    y: rand(-pad, opt.height + pad),
    age: 0,
    acceleration: rand(opt.acceleration[0], opt.acceleration[1]),
    color: opt.randColor
      ? `${rand(0, 255)},${rand(0, 255)},${rand(0, 255)}`
      : opt.color,
  }
}

/** Önceki kare konumunda çizer; hareket ve yaş burada güncellenir (orijinal davranış). */
function drawSpark(ctx: CanvasRenderingContext2D, spark: SparkParticle, opt: SparkOpt): boolean {
  const px = spark.x
  const py = spark.y
  spark.x += (opt.speed * opt.direction.x * spark.acceleration) / 2
  spark.y += (opt.speed * opt.direction.y * spark.acceleration) / 2
  spark.age += 1
  const opacity = opt.maxopacity - spark.age / opt.lifetime
  if (opacity <= 0) return false
  ctx.fillStyle = `rgba(${spark.color}, ${opacity})`
  ctx.fillRect(px, py, opt.size[0], opt.size[1])
  return true
}

export function SparkEffect({
  className,
  canvasId = 'sparks',
  amount = 5000,
  speed = 0.05,
  lifetime = 200,
  direction = { x: -0.5, y: 1 },
  size = [2, 2],
  maxopacity = 1,
  color = '150,150,150',
  randColor = true,
  acceleration = [5, 40],
}: SparkEffectProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const narrow = window.innerWidth < 520
    const optBase: Omit<SparkOpt, 'width' | 'height'> = {
      amount,
      speed: narrow ? 0.05 : speed,
      lifetime,
      direction: { ...direction },
      size: [...size] as [number, number],
      maxopacity,
      color: narrow ? '150,150,150' : color,
      randColor,
      acceleration: [...acceleration] as [number, number],
    }

    let sparks: SparkParticle[] = []
    let raf = 0
    let intervalId: ReturnType<typeof setInterval> | undefined

    const syncSize = () => {
      const w = wrap.clientWidth
      const h = wrap.clientHeight
      if (w <= 0 || h <= 0) return
      canvas.width = w
      canvas.height = h
    }

    const currentOpt = (): SparkOpt => ({
      ...optBase,
      width: wrap.clientWidth,
      height: wrap.clientHeight,
    })

    const addSpark = () => {
      const o = currentOpt()
      if (o.width <= 0 || o.height <= 0) return
      sparks.push(createSpark(o))
    }

    const draw = () => {
      const o = currentOpt()
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      sparks = sparks.filter((s) => drawSpark(ctx, s, o))
      raf = requestAnimationFrame(draw)
    }

    const startInterval = () => {
      const ms = Math.max(1000 / Math.max(optBase.amount, 1), 8)
      intervalId = setInterval(() => {
        const o = currentOpt()
        if (sparks.length < o.amount) addSpark()
      }, ms)
    }

    const ro = new ResizeObserver(() => {
      syncSize()
    })
    ro.observe(wrap)
    syncSize()
    startInterval()
    raf = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(raf)
      if (intervalId !== undefined) clearInterval(intervalId)
      ro.disconnect()
      sparks = []
    }
  }, [
    amount,
    speed,
    lifetime,
    direction.x,
    direction.y,
    size[0],
    size[1],
    maxopacity,
    color,
    randColor,
    acceleration[0],
    acceleration[1],
  ])

  return (
    <div ref={wrapRef} className={cn('pointer-events-none absolute inset-0', className)} aria-hidden>
      <canvas
        ref={canvasRef}
        id={canvasId}
        className="absolute left-0 top-0 h-full w-full bg-transparent"
      />
    </div>
  )
}
